import classNames from 'classnames';
import { isNumber } from 'lodash';
import React, { useEffect, useRef, useState } from 'react';
import { Scrollbars, SliderInput } from '@meitu/saas-ui';
import { CustomTitle } from '@meitu/saas-ui/es/title';
import { type HighlightMaterial } from '@meitu/saas-editor-core/es/framework/services/text-highlight';
import { DEFAULT_HIGHLIGHT_THICKNESS, type TextLayer } from '@meitu/saas-editor-core/es/layers/text-layer';
import { imageConverter } from '@meitu/saas-editor-core/es/utils/file-kit';
import { type ITextMark as PersistedHighlight } from '@meitu/saas-editor-core/es/version-types';
import IconFont from '@meitu/xdesign-icon-font';
import { ColorGroup } from '@meitu/xdesign-widgets';
import LabelAction from '@/components/label-action';
import { useEditor } from '@/hooks/use-editor';
import { useGlobalWidget } from '@/hooks/use-global-widget';
import { useTextRange, useTextRangeAttrsState } from '@/hooks/use-text-range-attrs-state';
import { useLocaleStore } from '@/i18n';
import iconPRO from '../../assets/icon-pro.png';
import iconVIP from '../../assets/icon-svip.png';
import DragPopover from '../../components/drag-popover';
import { useHighlight } from '../../hooks';
import { type WidgetCommonProps } from '../../types';
import { getHighlightThumbnailURL, Tracker } from '../../utils';
import './style.less';

const COLOR_PICKER_DOM_CLASS = 'style-paint-color-picker';

// FIXME: 划重点数据加载方式非常怪异，需要调整

type Props = WidgetCommonProps & {
    onClose: () => void;
    highlights: HighlightMaterial[];
};

export const HIGHLIGHT_POPOVER = 'highlight-popover';

export const HighlightPanel: React.FC<Props> = ({ onClose, layerId, recordId, isIntl, theme, highlights }) => {
    const { $t } = useLocaleStore();

    // 添加一个 key 来控制组件重新创建
    const [actionKey, setActionKey] = useState(0);

    const scrollbarsRef = useRef<any>(null);
    const draggleRef = useRef<HTMLDivElement>(null);
    const editor = useEditor();
    const textRange = useTextRange(layerId);
    const textRangeAttrsState = useTextRangeAttrsState(layerId);
    const { highlight, highlightsInTextContents } = useHighlight(editor);
    const popoverWidgetRef = useGlobalWidget(editor, false);

    const layerHighlight = highlight ?? (highlightsInTextContents.length === 1 ? highlightsInTextContents[0] : null);
    const hasSelection = textRange != null && textRange.start !== textRange.end;
    const hasRangeAttrs =
        hasSelection && (textRangeAttrsState?.type === 'uniform' || textRangeAttrsState?.type === 'mixed');
    const targetHighlight = hasRangeAttrs ? (textRangeAttrsState.attrs.textMark ?? null) : layerHighlight;
    const activeIndex = targetHighlight
        ? highlights.findIndex((item) => String(item.id) === String(targetHighlight.materialId))
        : -1;
    const targetHighlightConfig = activeIndex < 0 ? null : highlights[activeIndex];
    const actionInsertIndex =
        activeIndex < 0 ? -1 : Math.min(Math.floor((activeIndex + 1) / 3) * 3 + 2, highlights.length) - 1;

    const enableOpacity = targetHighlightConfig?.data.enableOpacity ?? true;

    const renderCornerIcon = () => {
        if (isIntl) {
            return <img className="highlight-panel__icon" src={iconPRO} />;
        }
        return <img className="highlight-panel__icon" src={iconVIP} />;
    };

    const setHighlight = (item: HighlightMaterial) => {
        if (String(targetHighlight?.materialId) === String(item.id)) return;

        // 先重置 actionKey 触发重新渲染（解决：如果打开了颜色面板，关不掉）
        setActionKey((prev) => prev + 1);

        const data: PersistedHighlight = {
            materialId: String(item.id),
            enableSize: item.data.enableSize,
            size: DEFAULT_HIGHLIGHT_THICKNESS,
            color: item.data.defaultColor,
        };
        editor.getInstance<TextLayer>('TextLayer').setHighlight(layerId, data);

        // eslint-disable-next-line react-hooks/rules-of-hooks
        Tracker.useTextMark({
            recordId,
            stageType: editor.services.editor.getCanvasType(),
        });
    };

    const clearHighlight = () => {
        editor.getInstance<TextLayer>('TextLayer').removeHighlight(layerId);
    };

    const handleOpenChange = (open: boolean) => {
        popoverWidgetRef.current = open;

        if (open) {
            Tracker.openColorPicker({
                recordId,
                stageType: editor.services.editor.getCanvasType(),
                kind: 'textMark',
            });
        }
    };

    const handleColorChange = (data: PersistedHighlight, value: string) => {
        let color = value;

        if (!enableOpacity) color = value.slice(0, 7);

        const highlight: PersistedHighlight = { ...data, color };
        editor.getInstance<TextLayer>('TextLayer').updateHighlight(layerId, highlight);
    };

    const handleColorAfterChange = (data: PersistedHighlight, value: string) => {
        let color = value;

        if (!enableOpacity) color = value.slice(0, 7);

        const highlight: PersistedHighlight = { ...data, color };
        editor.getInstance<TextLayer>('TextLayer').updateHighlight(layerId, highlight, {
            writeHistory: true,
        });

        Tracker.adjustTextMarkProps({
            recordId,
            stageType: editor.services.editor.getCanvasType(),
            isColor: true,
        });
    };

    const handleSizeChange = (data: PersistedHighlight, value: number) => {
        if (!data.enableSize || !isNumber(value)) return;

        const highlight: PersistedHighlight = { ...data, size: value };
        editor.getInstance<TextLayer>('TextLayer').updateHighlight(layerId, highlight);
    };

    const handleSizeAfterChange = (data: PersistedHighlight, value: number) => {
        if (!data.enableSize || !isNumber(value)) return;

        const highlight: PersistedHighlight = { ...data, size: value };
        editor.getInstance<TextLayer>('TextLayer').updateHighlight(layerId, highlight, {
            writeHistory: true,
        });

        Tracker.adjustTextMarkProps({
            recordId,
            stageType: editor.services.editor.getCanvasType(),
            isColor: false,
        });
    };

    //选择后，在对应行后面插入操作组件
    useEffect(() => {
        if (activeIndex < 0) return;
        const materialPosition = activeIndex + 2; // +1 转换为 CSS 索引，+1 跳过自定义的重置元素

        const activeEl = document.querySelector(
            `.highlight-panel__item:nth-of-type(${materialPosition})`,
        ) as HTMLElement;
        if (activeEl) requestAnimationFrame(() => scrollbarsRef.current?.scrollTop(activeEl.offsetTop - 16)); // 减去16px，是因为操作组件的padding-top为16px
    }, [activeIndex]);

    //  渲染操作组件
    const renderActions = () => {
        return targetHighlight && targetHighlightConfig ? (
            <div className="highlight-panel__actions">
                {/* 颜色 */}
                <LabelAction title={$t((v) => v.textMarkSelector.color)} align>
                    <ColorGroup
                        popupTriggerSize={{ width: 58, height: 26 }}
                        color={targetHighlight.color}
                        hideAlpha={!enableOpacity}
                        isGradient={false}
                        setColor={(color) => handleColorChange(targetHighlight, color)}
                        setColorAfter={(color, _, { isBrandColor }) => {
                            handleColorAfterChange(targetHighlight, color);
                            if (isBrandColor) {
                                window._stat_track?.('hb_layers_func_use', {
                                    func_id: '1_107',
                                });
                            }
                        }}
                        showIcon={false}
                        popupOverlayClassName={COLOR_PICKER_DOM_CLASS}
                        placement="topLeft"
                        onOpenChange={handleOpenChange}
                        onToggleBizTab={(bizTab) => {
                            if (bizTab === 'team') {
                                window._stat_track?.('hb_layers_func_click', {
                                    func_id: '1_107',
                                });
                            }
                        }}
                    />
                </LabelAction>

                {/* 粗细 */}
                {targetHighlight.enableSize ? (
                    <LabelAction title={$t((v) => v.textMarkSelector.size)} align>
                        <div style={{ flex: 1 }}>
                            <SliderInput
                                min={1}
                                max={100}
                                value={targetHighlight.size}
                                precision={1}
                                showStepBtn
                                type="horizontal"
                                onChange={(value) => handleSizeChange(targetHighlight, value)}
                                onAfterChange={(value) => handleSizeAfterChange(targetHighlight, value)}
                            />
                        </div>
                    </LabelAction>
                ) : null}
            </div>
        ) : null;
    };

    return (
        <DragPopover draggleRef={draggleRef} innerDoms={[COLOR_PICKER_DOM_CLASS]}>
            <div className="highlight-panel" ref={draggleRef}>
                <div className="shape-text-panel__title">
                    <CustomTitle
                        leftNode={<div className="left">{$t((v) => v.panel.textPanel.textMark)}</div>}
                        rightNode={
                            <div className="right" onClick={onClose}>
                                <IconFont name="CrossBlack" fontSize={12} color="var(--content-system-secondary)" />
                            </div>
                        }
                    />
                </div>

                {/* safari低版本需要包一层div 滚动列表才能撑开*/}
                <div style={{ flex: 1 }}>
                    <Scrollbars autoHide scrollbarsRef={scrollbarsRef}>
                        <div className="highlight-panel__list">
                            <div className="highlight-panel__materials">
                                <div className="highlight-panel__item" onClick={clearHighlight}>
                                    <IconFont name="SlashCircleBold" fontSize={28} />
                                </div>

                                {highlights.map((item, index) => {
                                    const active =
                                        targetHighlight && String(item.id) === String(targetHighlight.materialId);
                                    const cls = classNames('highlight-panel__item', {
                                        'highlight-panel__item--active': active,
                                    });
                                    const url = getHighlightThumbnailURL(item, theme, isIntl);
                                    const cover = url ? imageConverter(url, 140) : '';

                                    return (
                                        <React.Fragment key={item.id}>
                                            <div
                                                className={cls}
                                                key={item.id}
                                                style={{ backgroundImage: `url(${cover})` }}
                                                onClick={() => setHighlight(item)}
                                            >
                                                {item.isVip ? renderCornerIcon() : null}
                                            </div>
                                            {/* 插入操作组件 */}
                                            {actionInsertIndex === index && <p key={actionKey}>{renderActions()}</p>}
                                        </React.Fragment>
                                    );
                                })}
                            </div>
                        </div>
                    </Scrollbars>
                </div>
            </div>
        </DragPopover>
    );
};
